import uniqBy from 'lodash/uniqBy';

import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import pluck from '@watershed/shared-util/src/pluck';

import type {
  CalculationModel,
  UnboundVariable,
} from '../../calculationModel/calculationModelSchema';
import { GLOBAL_SCOPE_PREFIX } from '../../calculationModel/constants';
import {
  ActivityDataColumn,
  BatActivityDataColumn,
  CustomActivityDataColumn,
} from './activityDataColumns';
import type {
  CalculationModelMatcherBinding,
  CalculationModelParameterExpression,
} from './entities';
import { sortVariablesByDirectUse } from './expressionVariableUtils';

// - unbound : an input variable that exists on the calculation model
// - bound : a model scoped variable which might be customizable
// - custom-activity-data : a user defined custom activity data column that can be used for customization
// - optional-activity-data : an optional BART activity data column that can be used for customization
export type CalculationMethodVariableKind =
  | 'unbound'
  | 'bound'
  | 'custom-activity-data'
  | 'optional-activity-data';

export interface CalculationMethodVariable {
  name: string;
  displayName: string;
  type: string;
  unit: string | null;
  description: string | null;
  /**
   * The expression text to display for this variable.
   * When undefined, indicates the variable is set by an importing method
   * and the UI should display descriptive prose instead.
   */
  expressionColumnText: string | undefined;
  kind: CalculationMethodVariableKind;
}

export interface CalculationMethodVariableGroup {
  displayName: string;
  variables: Array<CalculationMethodVariable>;
}

/**
 * A map of parameter names to their parameter expressions.
 * Used for efficient lookup of parameter expressions by name.
 */
export type ParameterExpressions = Record<string, string>;

/**
 * Converts an array of parameter expressions to an object map for efficient lookup.
 * This is the canonical way to transform the array format (from entities/GraphQL)
 * into the map format expected by variable-related helpers.
 */
export function getParameterExpressionsMap(
  parameterExpressions: Array<CalculationModelParameterExpression> | undefined
): ParameterExpressions {
  return Object.fromEntries(
    (parameterExpressions ?? []).map(({ name, expression }) => [
      name,
      expression,
    ])
  );
}

/**
 * Extracts all bindings from an array of matchers.
 * Matchers link calculation methods to calculation models, and each matcher
 * contains bindings that map input variable names to expressions.
 */
export function getMatcherBindings(
  matchers: Array<{ bindings: Array<CalculationModelMatcherBinding> }>
): Array<CalculationModelMatcherBinding> {
  return matchers.flatMap((matcher) => matcher.bindings);
}

/**
 * Filters out variable groups that have no variables.
 * Useful when building variable group lists where some categories may be empty.
 */
export function filterEmptyVariableGroups<
  T extends { variables: Array<unknown> },
>(groups: Array<T>): Array<T> {
  return groups.filter((group) => group.variables.length > 0);
}

/**
 * Filters variables by a search term, matching against both displayName and name.
 * Case-insensitive partial match.
 */
export function filterVariablesBySearch(
  variables: Array<CalculationMethodVariable>,
  search: string
): Array<CalculationMethodVariable> {
  if (!search) {
    return variables;
  }

  const q = search.toLowerCase();
  return variables.filter((variable) => {
    const displayName = (variable.displayName || '').toLowerCase();
    const name = variable.name.toLowerCase();
    return displayName.includes(q) || name.includes(q);
  });
}

/**
 * A simple function, but we pull this out so we can do it the same way
 * everywhere we need to introspect/qualify a variable name for a given node (or
 * global prefix).
 */
export function qualifiedVariableForNode({
  nodeName,
  variableName,
}: {
  nodeName: string;
  variableName: string;
}): string {
  return `${nodeName}__${variableName}`;
}

/**
 * Creates a bound calculation variable from a scoped name, a variable, and a
 * parameter expression.
 */
const createBoundCalculationVariable = (
  scopedName: string,
  variable: UnboundVariable,
  parameterExpression: string
): CalculationMethodVariable => {
  return {
    name: scopedName,
    displayName: variable.displayName ?? variable.name,
    type: variable.type,
    unit: variable.unit,
    description: variable.description,
    expressionColumnText: parameterExpression,
    kind: 'bound',
  };
};

/**
 * Creates an unbound calculation variable from a name and a variable.
 * If a parameter expression is provided, uses that expression; otherwise
 * expressionColumnText is undefined to indicate it's set by an importing method.
 */
const createUnboundCalculationVariable = (
  variable: UnboundVariable,
  parameterExpression: string | undefined
): CalculationMethodVariable => {
  return {
    name: variable.name,
    displayName: variable.displayName ?? variable.name,
    type: variable.type,
    unit: variable.unit,
    description: variable.description,
    expressionColumnText: parameterExpression,
    kind: 'unbound',
  };
};

/**
 * Creates a custom activity data calculation variable from a custom column
 */
const createCustomActivityDataCalculationVariable = (
  variable: CustomActivityDataColumn
): CalculationMethodVariable => {
  return {
    name: variable.fieldName,
    displayName: variable.displayName ?? variable.fieldName,
    type: variable.type,
    unit: null,
    description: variable.description ?? null,
    expressionColumnText: variable.fieldName,
    kind: 'custom-activity-data',
  };
};

/**
 * Creates an optional activity data calculation variable from a BART activity
 * data column.
 */
const createOptionalActivityDataCalculationVariable = (
  variable: BatActivityDataColumn
): CalculationMethodVariable => {
  return {
    name: variable.fieldName,
    displayName: variable.displayName ?? variable.fieldName,
    type: variable.type,
    unit: null,
    description: variable.description ?? null,
    expressionColumnText: variable.fieldName,
    kind: 'optional-activity-data',
  };
};

/**
 * Converts an activity data column to a calculation method variable.
 */
const convertActivityDataColumnToCalculationMethodVariable = (
  variable: ActivityDataColumn
): CalculationMethodVariable => {
  if (variable.isCustom) {
    return createCustomActivityDataCalculationVariable(
      variable as CustomActivityDataColumn
    );
  }

  return createOptionalActivityDataCalculationVariable(
    variable as BatActivityDataColumn
  );
};

/**
 * Gets optional activity variables from the calculation methods.
 * Converts the activity data columns to calculation method variables.
 */
export const getOptionalActivityVariables = ({
  optionalActivityVariables,
}: {
  optionalActivityVariables: Array<ActivityDataColumn>;
}): Array<CalculationMethodVariable> => {
  return uniqBy(
    optionalActivityVariables.map(
      convertActivityDataColumnToCalculationMethodVariable
    ),
    pluck('name')
  );
};

/**
 * Gets global reference data variables from the calculation model by binding
 * the global variables to the parameter expressions. If an import param binding
 * is provided, it will be used to override the parameter expression.
 *
 * Filters out variables that do not have a parameter expression.
 */
const getGlobalRefDataVariables = ({
  calculationModel,
  parameterExpressions,
  importParamBindings,
}: {
  calculationModel: CalculationModel;
  parameterExpressions: ParameterExpressions;
  importParamBindings?: Record<string, string>;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.globalVariables)
    .map(([name, globalVariable]) => {
      const scopedName = qualifiedVariableForNode({
        nodeName: GLOBAL_SCOPE_PREFIX,
        variableName: name,
      });

      // If an import param binding is provided, use that since it overrides the
      // parameter expression. Otherwise, use the parameter expression.
      const expression =
        importParamBindings && importParamBindings[scopedName]
          ? importParamBindings[scopedName]
          : parameterExpressions[scopedName];

      if (!expression) {
        return null;
      }

      return createBoundCalculationVariable(
        scopedName,
        globalVariable,
        expression
      );
    })
    .filter(isNotNullish);
};

/**
 * Gets all local variables for a calculation model by binding the local
 * variables to the parameter expressions. If an import param binding is
 * provided, it will be used to override the parameter expression.
 *
 * If a variable does not have a parameter expression, it will be set to an
 * empty string.
 */
const getAllLocalVariables = ({
  calculationModel,
  parameterExpressions,
  importParamBindings,
}: {
  calculationModel: CalculationModel;
  parameterExpressions: ParameterExpressions;
  importParamBindings?: Record<string, string>;
}): Array<CalculationMethodVariable> => {
  return calculationModel.nodes.flatMap((node) => {
    return Object.entries(node.variables).flatMap(
      ([variableName, nodeVariable]) => {
        const scopedName = qualifiedVariableForNode({
          nodeName: node.name,
          variableName,
        });

        // If an import param binding is provided, use that since it overrides
        // the parameter expression. Otherwise, use the parameter expression.
        const expression =
          importParamBindings && importParamBindings[scopedName]
            ? importParamBindings[scopedName]
            : parameterExpressions[scopedName];

        return createBoundCalculationVariable(
          scopedName,
          nodeVariable,
          expression ?? ''
        );
      }
    );
  });
};

/**
 * Gets all input variables for a top-level calculation model by binding the
 * input variables to the matcher bindings.
 *
 * If an input variable does not have a matcher binding, expressionColumnText
 * will be undefined. This is expected for imported models which are reused
 * in other models.
 */
export const getInputVariablesForTopLevelModel = ({
  calculationModel,
  matcherBindings,
}: {
  calculationModel: CalculationModel;
  matcherBindings: Array<CalculationModelMatcherBinding>;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.inputVariables)
    .map(([_, inputVariable]) => {
      const matcherBinding = matcherBindings.find(
        (binding) => binding.name === inputVariable.name
      );

      return createUnboundCalculationVariable(
        inputVariable,
        matcherBinding?.expression
      );
    })
    .filter(isNotNullish);
};

/**
 * Gets all input variables for an imported descendant by binding the input
 * variables to the import param bindings.
 *
 * If an input variable does not have an import param binding, expressionColumnText
 * will be undefined.
 */
const getInputVariablesForImportedDescendant = ({
  calculationModel,
  importParamBindings,
}: {
  calculationModel: CalculationModel;
  importParamBindings: ParameterExpressions;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.inputVariables).map(
    ([_, inputVariable]) => {
      return createUnboundCalculationVariable(
        inputVariable,
        importParamBindings[inputVariable.name]
      );
    }
  );
};

/**
 * Gets all input, global ref data, optional activity, and local variables for a
 * top-level calculation model.
 *
 * Local variables are only included if they are actually bound (used).
 */
export const getAllVariablesForTopLevelCalculationModel = (
  {
    calculationModel,
    parameterExpressions,
    matcherBindings,
  }: {
    calculationModel: CalculationModel;
    parameterExpressions: ParameterExpressions;
    matcherBindings: Array<CalculationModelMatcherBinding>;
  },
  deps: {
    getAllLocalVariables: typeof getAllLocalVariables;
    getInputVariablesForTopLevelModel: typeof getInputVariablesForTopLevelModel;
    getGlobalRefDataVariables: typeof getGlobalRefDataVariables;
  } = {
    getAllLocalVariables,
    getInputVariablesForTopLevelModel,
    getGlobalRefDataVariables,
  }
): {
  localVariables: Array<CalculationMethodVariable>;
  inputVariables: Array<CalculationMethodVariable>;
  globalRefDataVariables: Array<CalculationMethodVariable>;
} => {
  // For top-level models, only include locals that are actually bound (used).
  const allLocalVariables = deps.getAllLocalVariables({
    calculationModel,
    parameterExpressions,
  });
  const localVariables = allLocalVariables.filter(
    (v) => v.expressionColumnText !== ''
  );

  return {
    localVariables,
    inputVariables: deps.getInputVariablesForTopLevelModel({
      calculationModel,
      matcherBindings,
    }),
    globalRefDataVariables: deps.getGlobalRefDataVariables({
      calculationModel,
      parameterExpressions,
    }),
  };
};

/**
 * Gets all input, global ref data, and local variables for an imported
 * descendant.
 */
export const getAllVariablesForImportedDescendant = (
  {
    calculationModel,
    parameterExpressions,
    importParamBindings,
  }: {
    calculationModel: CalculationModel;
    parameterExpressions: ParameterExpressions;
    importParamBindings: Record<string, string>;
  },
  deps: {
    getAllLocalVariables: typeof getAllLocalVariables;
    getInputVariablesForImportedDescendant: typeof getInputVariablesForImportedDescendant;
    getGlobalRefDataVariables: typeof getGlobalRefDataVariables;
  } = {
    getAllLocalVariables,
    getInputVariablesForImportedDescendant,
    getGlobalRefDataVariables,
  }
): {
  localVariables: Array<CalculationMethodVariable>;
  inputVariables: Array<CalculationMethodVariable>;
  globalRefDataVariables: Array<CalculationMethodVariable>;
} => {
  return {
    localVariables: deps.getAllLocalVariables({
      calculationModel,
      parameterExpressions,
      importParamBindings,
    }),
    inputVariables: deps.getInputVariablesForImportedDescendant({
      calculationModel,
      importParamBindings,
    }),
    globalRefDataVariables: deps.getGlobalRefDataVariables({
      calculationModel,
      parameterExpressions,
      importParamBindings,
    }),
  };
};

/**
 * Gets the names of variables that are customizable based on the customization
 * schema. A variable is customizable if it exists in both the schema's
 * parameters and the provided list of variables.
 *
 * @param schema The customization schema containing parameters (typically from customizationSchemaRevision?.schema)
 * @param allVariables The list of all variables to check against
 * @returns A set of variable names that are customizable
 */
export function getCustomizableVariableNames(
  schema: { parameters?: Array<{ name: string }> } | null | undefined,
  allVariables: Array<CalculationMethodVariable>
): Set<string> {
  const parameters = schema?.parameters;
  if (!parameters) {
    return new Set<string>();
  }

  const allVariableNames = new Set(allVariables.map((v) => v.name));
  return new Set(
    parameters.map((p) => p.name).filter((name) => allVariableNames.has(name))
  );
}

/**
 * Result of categorizing variables by their type/scope.
 */
export interface CategorizedVariables {
  /** Variables belonging to the selected node (step-scoped) */
  stepVariables: Array<CalculationMethodVariable>;
  /** Global variables shared across steps */
  sharedVariables: Array<CalculationMethodVariable>;
  /** Required activity data variables */
  activityVariables: Array<CalculationMethodVariable>;
  /** Optional activity data variables */
  optionalVariables: Array<CalculationMethodVariable>;
}

/**
 * Categorizes variables by their type/scope without any presentation concerns.
 * This is the pure logic for variable categorization that can be used by both
 * frontend (with icons/i18n) and backend code.
 *
 * Categories:
 * 1. Step variables - belong to the selected node (prefixed with nodeName__)
 * 2. Shared variables - global variables (prefixed with global__)
 * 3. Activity variables - required input variables (kind='unbound', no prefix)
 * 4. Optional variables - optional activity data (kind='optional-activity-data')
 *
 * @param variables The list of variables to categorize
 * @param selectedNodeName The name of the selected node
 * @param directVariableNames Optional set of directly-used variable names for sorting shared variables
 * @returns Categorized variables with shared variables sorted by direct use
 */
export function categorizeVariables({
  variables,
  selectedNodeName,
  directVariableNames,
}: {
  variables: Array<CalculationMethodVariable>;
  selectedNodeName: string;
  directVariableNames?: Set<string>;
}): CategorizedVariables {
  const stepPrefix = `${selectedNodeName}__`;
  const globalPrefix = `${GLOBAL_SCOPE_PREFIX}__`;

  const stepVariables: Array<CalculationMethodVariable> = [];
  const sharedVariables: Array<CalculationMethodVariable> = [];
  const activityVariables: Array<CalculationMethodVariable> = [];
  const optionalVariables: Array<CalculationMethodVariable> = [];

  for (const v of variables) {
    if (v.name.startsWith(stepPrefix)) {
      stepVariables.push(v);
      continue;
    }
    if (v.name.startsWith(globalPrefix)) {
      sharedVariables.push(v);
      continue;
    }
    if (v.kind === 'optional-activity-data') {
      optionalVariables.push(v);
      continue;
    }
    // Activity variables: kind is 'unbound' and doesn't have a node/global prefix
    if (v.kind === 'unbound' && !v.name.includes('__')) {
      activityVariables.push(v);
    }
  }

  // Sort shared variables: direct first, then transitive
  const orderedSharedVariables = sortVariablesByDirectUse({
    variables: sharedVariables,
    directVariableNames,
  });

  return {
    stepVariables,
    sharedVariables: orderedSharedVariables,
    activityVariables,
    optionalVariables,
  };
}

export const exportedForTesting = {
  createBoundCalculationVariable,
  createUnboundCalculationVariable,
  createCustomActivityDataCalculationVariable,
  createOptionalActivityDataCalculationVariable,
  convertActivityDataColumnToCalculationMethodVariable,
  getOptionalActivityVariables,
  getGlobalRefDataVariables,
  getAllLocalVariables,
  getInputVariablesForTopLevelModel,
  getInputVariablesForImportedDescendant,
};
